import { useState } from 'react';
import { useIntegrationHealth } from '@/hooks/useIntegrationHealth';
import { describeIntegrationState } from '@/lib/integrations/integration-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkline } from '@/components/ui/sparkline';
import { ChevronDown, ChevronUp, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { formatUiNumber, formatUiRelativeTime } from '@/lib/ui-format';
import { toSafeUiError } from '@/lib/user-safe-error';

function formatRelativeTime(timestamp: string | null): string {
  if (!timestamp) return 'Never';

  return formatUiRelativeTime(timestamp);
}

function StatusBadge({
  status,
  circuitBreaker,
}: {
  status: 'ok' | 'warning' | 'error';
  circuitBreaker: string;
}): React.ReactElement {
  if (circuitBreaker === 'open') {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertCircle className="h-3 w-3 animate-pulse" aria-hidden="true" />
        Circuit Breaker OPEN
      </Badge>
    );
  }
  
  if (circuitBreaker === 'half_open') {
    return (
      <Badge variant="default" className="gap-1 bg-yellow-500">
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        Testing Recovery
      </Badge>
    );
  }
  
  if (status === 'error') {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertCircle className="h-3 w-3" aria-hidden="true" />
        Error
      </Badge>
    );
  }
  
  if (status === 'warning') {
    return (
      <Badge variant="default" className="gap-1 bg-yellow-500">
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        Warning
      </Badge>
    );
  }
  
  return (
    <Badge variant="default" className="gap-1 bg-green-500">
      <CheckCircle className="h-3 w-3" aria-hidden="true" />
      Healthy
    </Badge>
  );
}

export function IntegrationHealthCard(): React.ReactElement {
  const {
    data: health,
    error,
    isFetching,
    isLoading,
    refetch,
  } = useIntegrationHealth();
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>HeyReach Connection</CardTitle>
          <CardDescription>Loading health status...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    const safeError = toSafeUiError(error);

    return (
      <Card>
        <CardHeader>
          <CardTitle>HeyReach Connection</CardTitle>
          <CardDescription>Integration health is temporarily unavailable.</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>{safeError.title}</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                {safeError.description} {safeError.recovery}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="min-h-11"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
              >
                {isFetching ? 'Retrying...' : 'Retry'}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!health) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>HeyReach Connection</CardTitle>
          <CardDescription>Integration status</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Health details are not available for this environment.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Not connected: an unpaid subscription is not an incident. Render a
  // neutral badge and the honest copy instead of an error alarm — retrying
  // cannot help, and `describeIntegrationState` already knows that.
  if (health.status === 'not_connected') {
    const { label, action } = describeIntegrationState({
      key: 'heyreach',
      status: 'not_connected',
    });

    return (
      <Card>
        <CardHeader>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>HeyReach Connection</CardTitle>
              <CardDescription>Integration status</CardDescription>
            </div>
            <Badge variant="secondary">{label}</Badge>
          </div>
        </CardHeader>
        <CardContent role="status">
          <p className="text-sm text-muted-foreground">
            {health.note ?? `${label}.`}
          </p>
          {action && <p className="mt-2 text-sm text-muted-foreground">{action}</p>}
        </CardContent>
      </Card>
    );
  }

  // Simplified view when health tracking is not fully enabled
  if (health.note) {
    return (
      <Card>
        <CardHeader>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>HeyReach Connection</CardTitle>
              <CardDescription>
                Integration status
              </CardDescription>
            </div>
            <Badge variant="default" className="gap-1 bg-green-500">
              <CheckCircle className="h-3 w-3" aria-hidden="true" />
              Connected
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            The integration is connected. Detailed health metrics are not enabled for this
            environment.
          </p>
        </CardContent>
      </Card>
    );
  }

  const successData = health.trend7d.map(d => d.webhooksOk + d.sendsOk);
  const failureData = health.trend7d.map(d => d.webhooksFail + d.sendsFail);
  let autoRefreshSeconds = 60;
  if (health.status === 'error') {
    autoRefreshSeconds = 10;
  } else if (health.status === 'warning') {
    autoRefreshSeconds = 30;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>HeyReach Connection</CardTitle>
            <CardDescription>
              Integration health and performance metrics
            </CardDescription>
          </div>
          <StatusBadge status={health.status} circuitBreaker={health.circuitBreaker} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm text-muted-foreground">Last webhook</p>
            <p className="text-lg font-medium">{formatRelativeTime(health.lastWebhookAt)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Last message sent</p>
            <p className="text-lg font-medium">{formatRelativeTime(health.lastSendAt)}</p>
          </div>
        </div>

        {/* 24h Stats */}
        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div>
            <p className="text-muted-foreground">Webhooks (24h)</p>
            <p className="font-medium">
              {formatUiNumber(health.stats24h.webhooks.total)} received
              {health.stats24h.webhooks.failed > 0 && (
                <span className="text-red-500 ml-1">
                  ({formatUiNumber(health.stats24h.webhooks.failed)} failed)
                </span>
              )}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Messages sent (24h)</p>
            <p className="font-medium">
              {formatUiNumber(health.stats24h.sends.total)} sent
              {health.stats24h.sends.failed > 0 && (
                <span className="text-red-500 ml-1">
                  ({formatUiNumber(health.stats24h.sends.failed)} failed)
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Sparkline Trend */}
        {successData.length > 0 && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">7-day activity trend</p>
            <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
              <div className="flex-1">
                <p className="text-xs text-green-500 mb-1">Success</p>
                <Sparkline
                  data={successData}
                  width={200}
                  height={30}
                  color="rgb(34, 197, 94)"
                />
              </div>
              {failureData.some((value) => value > 0) && (
                <div className="flex-1">
                  <p className="text-xs text-red-500 mb-1">Failures</p>
                  <Sparkline
                    data={failureData}
                    width={200}
                    height={30}
                    color="rgb(239, 68, 68)"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Business Impact Warning */}
        {health.impact24h.affectedDrafts > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              {formatUiNumber(health.impact24h.affectedDrafts)} draft{health.impact24h.affectedDrafts > 1 ? 's' : ''}
              {' '}affected by failures in the last 24 hours
            </AlertDescription>
          </Alert>
        )}

        {/* Expandable Details */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="min-h-11 w-full md:min-h-11"
            aria-expanded={expanded}
            aria-controls="heyreach-health-details"
          >
            {expanded ? (
              <>
                <ChevronUp className="mr-2 h-4 w-4" aria-hidden="true" />
                Hide details
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 h-4 w-4" aria-hidden="true" />
                Show details
              </>
            )}
          </Button>

          {expanded && (
            <div
              id="heyreach-health-details"
              className="mt-4 max-h-64 space-y-3 overflow-auto border-t pt-4 text-sm"
            >
              <div>
                <p className="font-medium mb-2">Error Rates (24h)</p>
                <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">Webhooks:</span>{' '}
                    <span className={health.stats24h.webhooks.errorRate > 5 ? 'text-red-500 font-medium' : ''}>
                      {formatUiNumber(health.stats24h.webhooks.errorRate, {
                        maximumFractionDigits: 1,
                      })}%
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Sends:</span>{' '}
                    <span className={health.stats24h.sends.errorRate > 5 ? 'text-red-500 font-medium' : ''}>
                      {formatUiNumber(health.stats24h.sends.errorRate, {
                        maximumFractionDigits: 1,
                      })}%
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <p className="font-medium mb-2">Latency (24h, p95)</p>
                <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">Webhook:</span>{' '}
                    {health.latency24h.webhookP95Ms
                      ? `${formatUiNumber(health.latency24h.webhookP95Ms, {
                          maximumFractionDigits: 0,
                        })} ms`
                      : 'N/A'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Send:</span>{' '}
                    {health.latency24h.sendP95Ms
                      ? `${formatUiNumber(health.latency24h.sendP95Ms, {
                          maximumFractionDigits: 0,
                        })} ms`
                      : 'N/A'}
                  </div>
                </div>
              </div>

              <div className="text-xs text-muted-foreground pt-2 border-t">
                <p>Circuit Breaker: <span className="font-medium">{health.circuitBreaker.toUpperCase()}</span></p>
                <p className="mt-1">
                  Auto-refreshes every {autoRefreshSeconds} seconds
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
